import React, { useState, useEffect, useMemo } from 'react'
import { User, CreateUserDto, UpdateUserDto, UserRole } from '@/types'
import { ROLE_LABELS, isBillableActor } from '@/utils/role-access'
import {
  validateEmail,
  validateHexColor,
  validateIban,
  validatePassword,
  validatePhone,
  validateSiret,
} from '@/utils/validation'
import { Modal } from '@/components/ui/Modal'
import { FormStepper } from '@/components/ui/FormStepper'
import { Input } from '@/components/ui/Input'
import { ColorPickerInput } from '@/components/ui/ColorPickerInput'
import { FileInput } from '@/components/ui/FileInput'
import { toastError, toastSuccess } from '@/utils/toast'

interface UserFormModalProps {
  user: User | null
  onClose: () => void
  onSuccess: () => void
  fixedRole?: UserRole
  allowedRoles?: UserRole[]
  onCreate: (data: CreateUserDto) => Promise<User>
  onUpdate: (id: string, data: UpdateUserDto) => Promise<User>
  createTitle?: string
  editTitle?: string
  autoGeneratePassword?: boolean
  requireBillingFields?: boolean
  showBrandingFields?: boolean
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  user,
  onClose,
  onSuccess,
  fixedRole,
  allowedRoles,
  onCreate,
  onUpdate,
  createTitle = 'Nouvel utilisateur',
  editTitle = "Modifier l'utilisateur",
  autoGeneratePassword = false,
  requireBillingFields = false,
  showBrandingFields = false,
}) => {
  const roles = allowedRoles ?? (fixedRole ? [fixedRole] : (Object.values(UserRole) as UserRole[]))
  const defaultRole = fixedRole ?? roles[0]

  const steps = useMemo(() => {
    const labels = ['Identité', 'Accès']
    if (requireBillingFields) labels.push('Facturation')
    if (showBrandingFields) labels.push('Personnalisation')
    return labels
  }, [requireBillingFields, showBrandingFields])

  const emptyFormData = (): CreateUserDto => ({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    role: defaultRole,
    password: '',
    companyName: '',
    infos: '',
    address: '',
    city: '',
    postalCode: '',
    siret: '',
    accountOwner: '',
    iban: '',
    bic: '',
    primaryColor: '',
    secondaryColor: '',
    logo: undefined,
  })

  const isEditMode = !!user

  const [currentStep, setCurrentStep] = useState(0)
  const [highestStepReached, setHighestStepReached] = useState(0)
  const [formData, setFormData] = useState<CreateUserDto>(emptyFormData())
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setCurrentStep(0)
    setHighestStepReached(isEditMode ? steps.length - 1 : 0)
    if (user) {
      setFormData({
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        phone: user.phone,
        role: user.role,
        password: '',
        companyName: user.companyName ?? '',
        infos: user.infos ?? '',
        address: user.address ?? '',
        city: user.city ?? '',
        postalCode: user.postalCode ?? '',
        siret: user.siret ?? '',
        accountOwner: user.accountOwner ?? '',
        iban: user.iban ?? '',
        bic: user.bic ?? '',
        primaryColor: user.primaryColor ?? '',
        secondaryColor: user.secondaryColor ?? '',
        logo: user.logo,
      })
      setLogoFile(null)
    } else {
      setFormData(emptyFormData())
      setLogoFile(null)
    }
  }, [user, defaultRole, steps.length, isEditMode])

  const canNavigateToStep = (step: number) => isEditMode || step <= highestStepReached

  const handleStepClick = (step: number) => {
    if (!canNavigateToStep(step)) return
    setCurrentStep(step)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const getStepKind = (step: number): 'identity' | 'access' | 'billing' | 'branding' => {
    let index = 0
    if (step === index++) return 'identity'
    if (step === index++) return 'access'
    if (requireBillingFields) {
      if (step === index++) return 'billing'
    }
    return 'branding'
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}
    const kind = getStepKind(step)

    if (kind === 'identity') {
      if (!formData.firstname.trim()) newErrors.firstname = 'Le prénom est requis'
      if (!formData.lastname.trim()) newErrors.lastname = 'Le nom est requis'
      if (!formData.email.trim()) {
        newErrors.email = "L'email est requis"
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Email invalide'
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Le téléphone est requis'
      } else if (!validatePhone(formData.phone)) {
        newErrors.phone = 'Le téléphone doit commencer par 06 ou 07 et contenir 10 chiffres'
      }
    }

    if (kind === 'access') {
      if (!autoGeneratePassword && !user && !formData.password) {
        newErrors.password = 'Le mot de passe est requis'
      } else if (formData.password && !validatePassword(formData.password)) {
        newErrors.password =
          'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial'
      }
    }

    if (kind === 'billing') {
      if (!formData.companyName?.trim()) {
        newErrors.companyName = 'Le nom commercial est requis'
      }
      if (!formData.address?.trim()) {
        newErrors.address = "L'adresse de facturation est requise"
      }
      if (!formData.city?.trim()) newErrors.city = 'La ville est requise'
      if (!formData.postalCode?.trim()) newErrors.postalCode = 'Le code postal est requis'
      if (!formData.siret?.trim()) {
        newErrors.siret = 'Le SIRET est requis'
      } else if (!validateSiret(formData.siret)) {
        newErrors.siret = 'Le SIRET doit contenir 14 chiffres'
      }
      const iban = formData.iban?.trim() ?? ''
      if (iban && !validateIban(iban)) newErrors.iban = 'IBAN invalide'
      if (iban && !formData.accountOwner?.trim()) {
        newErrors.accountOwner = 'Le titulaire du compte est requis avec un IBAN'
      }
    }

    if (kind === 'branding') {
      if (formData.primaryColor && !validateHexColor(formData.primaryColor)) {
        newErrors.primaryColor = 'Couleur invalide (format attendu : #RRGGBB)'
      }
      if (formData.secondaryColor && !validateHexColor(formData.secondaryColor)) {
        newErrors.secondaryColor = 'Couleur invalide (format attendu : #RRGGBB)'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validate = (): boolean => {
    for (let step = 0; step < steps.length; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step)
        return false
      }
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep(currentStep)) return
    const nextStep = Math.min(currentStep + 1, steps.length - 1)
    setHighestStepReached(prev => Math.max(prev, nextStep))
    setCurrentStep(nextStep)
  }

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const dataToSend: CreateUserDto | UpdateUserDto = {
        ...formData,
        role: fixedRole ?? formData.role,
      }
      if (user && !dataToSend.password) {
        delete (dataToSend as UpdateUserDto).password
      }
      if (!user && autoGeneratePassword) {
        delete (dataToSend as CreateUserDto).password
      }

      const includeActorFields = requireBillingFields || showBrandingFields

      if (includeActorFields) {
        if (!requireBillingFields) {
          delete dataToSend.companyName
          delete dataToSend.address
          delete dataToSend.city
          delete dataToSend.postalCode
          delete dataToSend.siret
          delete dataToSend.accountOwner
          delete dataToSend.iban
          delete dataToSend.bic
        }
        if (!showBrandingFields) {
          delete dataToSend.infos
          delete dataToSend.primaryColor
          delete dataToSend.secondaryColor
          delete dataToSend.logo
        } else {
          if (!dataToSend.primaryColor) delete dataToSend.primaryColor
          if (!dataToSend.secondaryColor) delete dataToSend.secondaryColor
          if (!dataToSend.infos?.trim()) delete dataToSend.infos
          if (!dataToSend.logo?.data) delete dataToSend.logo
        }
      } else {
        delete dataToSend.companyName
        delete dataToSend.infos
        delete dataToSend.address
        delete dataToSend.city
        delete dataToSend.postalCode
        delete dataToSend.siret
        delete dataToSend.accountOwner
        delete dataToSend.iban
        delete dataToSend.bic
        delete dataToSend.primaryColor
        delete dataToSend.secondaryColor
        delete dataToSend.logo
      }

      if (!dataToSend.accountOwner?.trim()) delete dataToSend.accountOwner
      if (dataToSend.iban?.trim()) {
        dataToSend.iban = dataToSend.iban.replace(/\s/g, '')
      } else {
        delete dataToSend.iban
      }
      if (!dataToSend.bic?.trim()) delete dataToSend.bic

      if (user) {
        await onUpdate(user._id, dataToSend as UpdateUserDto)
        toastSuccess('Utilisateur modifié avec succès')
      } else {
        await onCreate(dataToSend as CreateUserDto)
        toastSuccess(
          autoGeneratePassword
            ? 'Utilisateur créé. Les identifiants ont été envoyés par email.'
            : 'Utilisateur créé avec succès'
        )
      }
      onSuccess()
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur lors de l'enregistrement de l'utilisateur"
      toastError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const showRoleSelect = !fixedRole && roles.length > 1
  const stepKind = getStepKind(currentStep)
  const isLastStep = currentStep === steps.length - 1
  const submitLabel = isEditMode ? 'Modifier' : 'Créer'

  const handleLogoChange = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      toastError('Seuls les fichiers image (PNG, JPG, SVG, WebP) sont autorisés')
      return
    }

    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      setFormData(prev => ({
        ...prev,
        logo: {
          filename: file.name,
          data: base64,
        },
      }))
      if (errors.logo) {
        setErrors(prev => {
          const next = { ...prev }
          delete next.logo
          return next
        })
      }
    }
    reader.onerror = () => {
      toastError('Erreur lors de la lecture du logo')
    }
    reader.readAsDataURL(file)
  }

  const handleLogoRemove = () => {
    setLogoFile(null)
    setFormData(prev => ({
      ...prev,
      logo: undefined,
    }))
  }

  return (
    <Modal
      title={user ? editTitle : createTitle}
      confirmButtonText={isLastStep ? submitLabel : 'Suivant'}
      onCancel={onClose}
      onConfirm={isLastStep ? handleSubmit : handleNext}
      onBack={currentStep > 0 ? handleBack : undefined}
      secondaryAction={
        isEditMode && !isLastStep ? { text: submitLabel, onClick: handleSubmit } : undefined
      }
      isLoading={loading}
    >
      <form
        onSubmit={e => {
          e.preventDefault()
          if (isLastStep) handleSubmit()
          else handleNext()
        }}
        className="space-y-6"
      >
        {steps.length > 1 && (
          <FormStepper
            steps={steps}
            currentStep={currentStep}
            canNavigateToStep={canNavigateToStep}
            onStepClick={handleStepClick}
          />
        )}

        {stepKind === 'identity' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Prénom"
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                error={errors.firstname}
                required
              />
              <Input
                label="Nom"
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                error={errors.lastname}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
              />
              <Input
                label="Téléphone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                required
                placeholder="06XXXXXXXX ou 07XXXXXXXX"
              />
            </div>

            {showRoleSelect && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                  Rôle <span className="text-[var(--color-error)]">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 text-base border rounded-lg transition-all duration-200 focus:outline-none focus:border-[var(--color-primary)] ${
                    errors.role ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
                  }`}
                >
                  {roles.map(role => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1.5 text-sm text-[var(--color-error)]">{errors.role}</p>
                )}
              </div>
            )}
          </>
        )}

        {stepKind === 'access' && (
          <>
            {!autoGeneratePassword || user ? (
              <Input
                label="Mot de passe"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                required={!user && !autoGeneratePassword}
                helperText={user ? '(Laisser vide pour ne pas modifier)' : undefined}
              />
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)]">
                Un mot de passe sera généré automatiquement et envoyé par email à
                l&apos;utilisateur.
              </p>
            )}
          </>
        )}

        {stepKind === 'billing' && (
          <>
            <Input
              label="Nom commercial"
              type="text"
              name="companyName"
              value={formData.companyName ?? ''}
              onChange={handleChange}
              error={errors.companyName}
              required
              helperText="Nom affiché pour l'identité visuelle (marque blanche)"
            />

            <Input
              label="Adresse de facturation"
              type="text"
              name="address"
              value={formData.address ?? ''}
              onChange={handleChange}
              error={errors.address}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Code postal"
                type="text"
                name="postalCode"
                value={formData.postalCode ?? ''}
                onChange={handleChange}
                error={errors.postalCode}
                required
              />
              <Input
                label="Ville"
                type="text"
                name="city"
                value={formData.city ?? ''}
                onChange={handleChange}
                error={errors.city}
                required
              />
            </div>

            <Input
              label="SIRET"
              type="text"
              name="siret"
              value={formData.siret ?? ''}
              onChange={handleChange}
              error={errors.siret}
              required
              placeholder="14 chiffres"
              helperText="Obligatoire pour créer la fiche de facturation Flux"
            />

            <Input
              label="Titulaire du compte"
              type="text"
              name="accountOwner"
              value={formData.accountOwner ?? ''}
              onChange={handleChange}
              error={errors.accountOwner}
              helperText="Coordonnées bancaires (RIB) — facultatif"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="IBAN"
                type="text"
                name="iban"
                value={formData.iban ?? ''}
                onChange={handleChange}
                error={errors.iban}
                placeholder="FR76 ..."
              />
              <Input
                label="BIC"
                type="text"
                name="bic"
                value={formData.bic ?? ''}
                onChange={handleChange}
                error={errors.bic}
              />
            </div>

            {user?.fluxId != null && isBillableActor(user) && (
              <Input
                label="Identifiant Flux"
                type="text"
                name="fluxIdDisplay"
                value={String(user.fluxId)}
                disabled
                helperText="Créé automatiquement dans Flux lors de la création du compte"
              />
            )}
          </>
        )}

        {stepKind === 'branding' && (
          <>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                Informations complémentaires
              </label>
              <textarea
                name="infos"
                value={formData.infos ?? ''}
                onChange={e => {
                  const { value } = e.target
                  setFormData(prev => ({ ...prev, infos: value }))
                }}
                rows={3}
                className="w-full px-4 py-2.5 text-base border border-[var(--color-border)] rounded-lg transition-all duration-200 focus:outline-none focus:border-[var(--color-primary)]"
                placeholder="Adresse, coordonnées de facturation, etc."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ColorPickerInput
                label="Couleur principale"
                value={formData.primaryColor ?? ''}
                onChange={color => {
                  setFormData(prev => ({ ...prev, primaryColor: color }))
                  if (errors.primaryColor) {
                    setErrors(prev => {
                      const next = { ...prev }
                      delete next.primaryColor
                      return next
                    })
                  }
                }}
                error={errors.primaryColor}
                placeholder="#08b2b3"
                fallbackColor="#08b2b3"
                helperText="Sélecteur ou saisie manuelle (#RRGGBB)"
              />
              <ColorPickerInput
                label="Couleur secondaire"
                value={formData.secondaryColor ?? ''}
                onChange={color => {
                  setFormData(prev => ({ ...prev, secondaryColor: color }))
                  if (errors.secondaryColor) {
                    setErrors(prev => {
                      const next = { ...prev }
                      delete next.secondaryColor
                      return next
                    })
                  }
                }}
                error={errors.secondaryColor}
                placeholder="#078aa8"
                fallbackColor="#078aa8"
                helperText="Sélecteur ou saisie manuelle (#RRGGBB)"
              />
            </div>

            <div>
              <FileInput
                label="Logo"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleLogoChange}
                onFileRemove={handleLogoRemove}
                selectedFiles={logoFile ? [logoFile] : []}
                storedPreview={logoFile ? undefined : formData.logo}
                previewAlt={formData.companyName || 'Logo'}
                helperText="Image utilisée pour la personnalisation marque blanche"
              />
            </div>
          </>
        )}
      </form>
    </Modal>
  )
}
